import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { gmpService } from '../../api/gmpService';
import { mapGmpItem } from '../../utils/gmpDataMapper';
import { mapApiItemsToBoardRows } from '../../utils/gmpBoard';

/**
 * Async thunk to fetch grey-market premium list
 * GET /api/market/gmp?force=false
 *
 * options.maxAgeMs: skip the request when data this recent is already loaded
 * (the landing section and the GMP page share this data).
 */
export const fetchGmp = createAsyncThunk(
  'gmp/fetchGmp',
  async ({ force = false } = {}, { rejectWithValue }) => {
    try {
      const data = await gmpService.getGmp(force);
      if (data?.success === false) {
        return rejectWithValue(data.error || 'Failed to fetch GMP data');
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch GMP data');
    }
  },
  {
    condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
      const { status, fetchedAt } = getState().gmp;
      // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
      if (status === 'loading') return false;
      if (maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs) {
        return false;
      }
      return true;
    },
  }
);

/**
 * Async thunk to fetch GMP for a single company
 * GET /api/market/gmp/{company_name}
 *
 * options.slug: URL slug or company name (the API matches loosely)
 * options.maxAgeMs: skip the request when this company was fetched recently
 */
export const fetchGmpCompany = createAsyncThunk(
  'gmp/fetchGmpCompany',
  async ({ slug }, { rejectWithValue }) => {
    try {
      const data = await gmpService.getGmpByCompany(slug);
      if (data?.success === false || !data?.data) {
        return rejectWithValue({ message: data?.error || 'No GMP data found for this IPO', notFound: true });
      }
      return data.data;
    } catch (error) {
      return rejectWithValue({
        message: error.message || 'Failed to fetch GMP data',
        notFound: error.status === 404,
      });
    }
  },
  {
    condition: ({ slug, maxAgeMs = 0 } = {}, { getState }) => {
      if (!slug) return false;
      const entry = getState().gmp.companies[slug];
      if (entry?.status === 'loading') return false;
      if (maxAgeMs && entry?.status === 'succeeded' && Date.now() - entry.fetchedAt < maxAgeMs) {
        return false;
      }
      return true;
    },
  }
);

const initialState = {
  // Raw items from the API; views map them through the selectors below
  items: [],
  refreshedAt: null,
  source: null,
  fetchedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,

  // Single-company lookups keyed by slug: { item, status, error, notFound, fetchedAt }
  companies: {},
};

export const gmpSlice = createSlice({
  name: 'gmp',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGmp.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchGmp.fulfilled, (state, action) => {
        const data = action.payload?.data || {};
        state.status = 'succeeded';
        state.items = Array.isArray(data.items) ? data.items : [];
        state.refreshedAt = data.refreshed_at || null;
        state.source = data.source || null;
        state.fetchedAt = Date.now();
      })
      .addCase(fetchGmp.rejected, (state, action) => {
        // Keep previously loaded items so a failed refresh doesn't blank the page
        state.status = 'failed';
        state.error = action.payload || 'Failed to load GMP data';
      })

      .addCase(fetchGmpCompany.pending, (state, action) => {
        const { slug } = action.meta.arg;
        state.companies[slug] = { ...state.companies[slug], status: 'loading', error: null, notFound: false };
      })
      .addCase(fetchGmpCompany.fulfilled, (state, action) => {
        const { slug } = action.meta.arg;
        state.companies[slug] = {
          item: action.payload,
          status: 'succeeded',
          error: null,
          notFound: false,
          fetchedAt: Date.now(),
        };
      })
      .addCase(fetchGmpCompany.rejected, (state, action) => {
        const { slug } = action.meta.arg;
        // Keep any earlier item so a failed refresh still shows the last data
        state.companies[slug] = {
          ...state.companies[slug],
          status: 'failed',
          error: action.payload?.message || action.error?.message || 'Failed to load GMP data',
          notFound: Boolean(action.payload?.notFound),
        };
      });
  },
});

const selectGmpItems = (state) => state.gmp.items;

// Rows for the landing-page GMP section
export const selectGmpSectionRows = createSelector([selectGmpItems], (items) =>
  items.map(mapGmpItem).filter(Boolean)
);

// Rows for the full GMP board page (/ipo-gmp)
export const selectGmpBoardRows = createSelector([selectGmpItems], (items) =>
  mapApiItemsToBoardRows(items)
);

export const selectGmpCompanyEntry = (state, slug) => state.gmp.companies[slug];

export default gmpSlice.reducer;
